import { ref } from "vue";
import { supabase } from "../lib/supabase";
import { normalizeFotoContextLabel } from "../utils/fotoContextLabels";
import { resolveStorageUrl } from "../utils/storageUrl";
import { useToast } from "../composables/useToast";
import type { StatusEntry } from "../../../../types";

interface BundelWithOffertes {
  id: string;
  bundel_nr: string;
  naam?: string | null;
  ibedrijf_id: string;
  korting_percentage?: number | null;
  korting_bedrag?: number | null;
  status: StatusEntry[];
  handtekening?: string | null;
  getekend_door?: string | null;
  getekend_door_opdrachtgever?: {
    naam?: string | null;
  } | null;
  accept_datum?: string | null;
  factuur_adres_id?: string | null;
  created_at: string;
  omschrijving?: string | null;
  offertes?: any[];
}

/**
 * Composable for managing offerte bundels in the portal
 * Opdrachtgevers can view and sign bundels via this composable
 */
export function useOfferteBundels(opdrachtgeverId: string) {
  const bundels = ref<BundelWithOffertes[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const toast = useToast();

  async function fetchBundels() {
    loading.value = true;
    error.value = null;

    try {
      // First get bundel IDs for this opdrachtgever via junction table
      const { data: bundelLinks, error: junctionError } = await supabase
        .from("bundels_opdrachtgevers")
        .select("bundel_id")
        .eq("opdrachtgever_id", opdrachtgeverId);

      if (junctionError) throw junctionError;

      if (!bundelLinks || bundelLinks.length === 0) {
        bundels.value = [];
        return;
      }

      const bundelIds = bundelLinks.map((link) => link.bundel_id);

      // Fetch bundels with their offertes
      const { data, error: fetchError } = await supabase
        .from("offerte_bundels")
        .select(
          `
          *,
          offertes:offertes!offertes_bundel_id_fkey(
            id,
            offerteNr,
            offerteDatum,
            totaalIncKorting,
            status,
            offerte_producten(
              product_type,
              voorgestelde_prijs,
              aangepaste_prijs,
              aantal,
              opmerking,
              uitvoer_datum
            )
          )
        `,
        )
        .in("id", bundelIds)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;

      // Transform data to flatten offertes
      bundels.value = (data || []).map((bundel: any) => ({
        ...bundel,
        status: bundel.status || [],
        offertes: (bundel.offertes || []).map((offerte: any) => ({
          ...offerte,
          producten: Array.isArray(offerte.offerte_producten)
            ? offerte.offerte_producten.flatMap((product: any) => {
                const rows = [
                  {
                    productType: product.product_type,
                    prijsType: "voorgesteld",
                    prijs: product.voorgestelde_prijs || 0,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  },
                ];
                if (product.aangepaste_prijs !== null && product.aangepaste_prijs !== undefined) {
                  rows.push({
                    productType: product.product_type,
                    prijsType: "aangepast",
                    prijs: product.aangepaste_prijs,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  });
                }
                return rows;
              })
            : [],
        })),
      })) as BundelWithOffertes[];
    } catch (err) {
      const message = err instanceof Error ? err.message : "Fout bij ophalen bundels";
      error.value = message;
      console.error("Error fetching bundels:", err);
    } finally {
      loading.value = false;
    }
  }

  async function getBundelById(bundelId: string) {
    loading.value = true;
    error.value = null;

    try {
      const { data, error: fetchError } = await supabase
        .from("offerte_bundels")
        .select(
          `
          *,
          getekend_door_opdrachtgever:opdrachtgevers!offerte_bundels_getekend_door_fkey(
            naam
          ),
          offertes:offertes!offertes_bundel_id_fkey(
            id,
            offerteNr,
            offerteDatum,
            korting,
            kortingBedrag,
            kortingPerc,
            totaalIncKorting,
            status,
            offerte_producten(
              product_type,
              voorgestelde_prijs,
              aangepaste_prijs,
              aantal,
              opmerking,
              uitvoer_datum
            ),
            voorwaarden,
            iplan,
            basis,
            locatie:locatie_id(
              id,
              naam,
              contactNaam,
              straat,
              huisnr,
              postcode,
              plaats,
              bouwJaar,
              installatieJaar,
              pvInstallatieJaar,
              gebruiksDoel,
              branche,
              contactMail,
              telnr
            )
          )
        `,
        )
        .eq("id", bundelId)
        .single();

      if (fetchError) throw fetchError;

      if (!data) return null;

      const result = {
        ...(data as any),
        status: (data as any).status || [],
        offertes: ((data as any).offertes || []).map((offerte: any) => ({
          ...offerte,
          producten: Array.isArray(offerte.offerte_producten)
            ? offerte.offerte_producten.flatMap((product: any) => {
                const rows = [
                  {
                    productType: product.product_type,
                    prijsType: "voorgesteld",
                    prijs: product.voorgestelde_prijs || 0,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  },
                ];
                if (product.aangepaste_prijs !== null && product.aangepaste_prijs !== undefined) {
                  rows.push({
                    productType: product.product_type,
                    prijsType: "aangepast",
                    prijs: product.aangepaste_prijs,
                    aantal: product.aantal,
                    opmerking: product.opmerking,
                    uitvoerDatum: product.uitvoer_datum,
                  });
                }
                return rows;
              })
            : [],
        })),
      } as BundelWithOffertes;

      // Fetch fotoBoven photos for each offerte's locatie
      const locatieIds = result.offertes?.map((o: any) => o.locatie?.id).filter(Boolean) as string[];

      if (locatieIds?.length) {
        const { data: fotosData, error: fotosError } = await supabase
          .schema("rapport_details")
          .from("fotos")
          .select("id, remote_url, context_label, locatie_id")
          .in("locatie_id", locatieIds);

        if (!fotosError && fotosData) {
          const fotosByLocatie = fotosData.reduce((acc: Record<string, any[]>, foto: any) => {
            if (!acc[foto.locatie_id]) acc[foto.locatie_id] = [];
            acc[foto.locatie_id].push({
              ...foto,
              context_label: normalizeFotoContextLabel(foto.context_label),
              remote_url: foto.remote_url ? resolveStorageUrl(foto.remote_url) : foto.remote_url,
            });
            return acc;
          }, {});
          result.offertes?.forEach((offerte: any) => {
            if (offerte.locatie?.id) {
              offerte.locatie.fotoBoven_foto = fotosByLocatie[offerte.locatie.id] || [];
            }
          });
        }
      }

      return result;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Fout bij ophalen bundel";
      error.value = message;
      console.error("Error fetching bundel:", err);
      return null;
    } finally {
      loading.value = false;
    }
  }

  async function signBundel(bundelId: string, handtekening: string): Promise<boolean> {
    loading.value = true;
    error.value = null;

    try {
      const { error: rpcError } = await supabase.rpc("sign_bundel", {
        p_bundel_id: bundelId,
        p_handtekening: handtekening,
      });

      if (rpcError) throw rpcError;

      // Notifications (ibedrijf + opdrachtgever email) are enqueued by a DB
      // trigger inside sign_bundel's update and delivered by the
      // process-notification-queue worker.
      toast.success("Bundel succesvol getekend");
      await fetchBundels();
      return true;
    } catch (err) {
      const message = err instanceof Error ? err.message : "Fout bij tekenen bundel";
      error.value = message;
      toast.error(message);
      console.error("Error signing bundel:", err);
      return false;
    } finally {
      loading.value = false;
    }
  }

  // Get bundels that are pending signature - check if any status entry has 'alles_geaccepteerd'
  function getPendingBundels() {
    return bundels.value.filter((b) => {
      const statuses = b.status || [];
      return !statuses.some((s) => s.status === "alles_geaccepteerd");
    });
  }

  // Get signed bundels
  function getSignedBundels() {
    return bundels.value.filter((b) => {
      const statuses = b.status || [];
      return statuses.some((s) => s.status === "alles_geaccepteerd");
    });
  }

  // Record that the opdrachtgever opened this bundel in the portal.
  // Fire-and-forget: tracking must never block or break the view.
  async function markBundelBekeken(bundelId: string): Promise<void> {
    try {
      const { error: rpcError } = await supabase.rpc("mark_bundel_bekeken", {
        p_bundel_id: bundelId,
      });
      if (rpcError) throw rpcError;
    } catch (err) {
      console.error("Failed to mark bundel as bekeken:", err);
    }
  }

  return {
    bundels,
    loading,
    error,
    fetchBundels,
    getBundelById,
    signBundel,
    getPendingBundels,
    getSignedBundels,
    markBundelBekeken,
  };
}
